import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-60px 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.7, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

/* Masked, line-by-line reveal — plays on mount (for above-the-fold headings) */
export function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className="reveal-mask">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.85, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* Masked reveal that fires reliably when scrolled into view (uses useInView hook) */
export function MaskLineInView({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12% 0px -12% 0px" });
  return (
    <motion.span
      ref={ref}
      className={`block ${className}`}
      initial={{ y: 40, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : { y: 40, opacity: 0 }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  );
}

/* Animated section eyebrow — slides in from the left with a growing rule */
export function Eyebrow({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -26 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -26 }}
      transition={{ duration: 0.6, ease: EASE }}
      className={`flex items-center gap-3 mb-4 ${className}`}
    >
      <motion.span
        className="h-[3px] bg-wcc-red block"
        initial={{ width: 0 }}
        animate={inView ? { width: 30 } : { width: 0 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
      />
      <span className="font-display text-sm sm:text-base font-semibold uppercase tracking-[0.14em] text-wcc-red">
        {children}
      </span>
    </motion.div>
  );
}
